import React, { useCallback, useEffect, useRef } from "react";
import { Modal, Form, Row, Col, Input, Select, Switch, Spin } from "antd";
import { Formik } from "formik";
import * as Yup from "yup";
import NumberFormat from "react-number-format";
import useStockItemContext from "../../../hooks/useStockItemContext";
import { useSelector } from "react-redux";
import {
  SUPPLEMENT_GROUPS,
  SUPPLEMENT_SUB_GROUPS,
  SUPPLEMENT_PRESENTATION,
  VETERINARY_TYPES,
  VETERINARY_APPLICATION_GENDER_TYPES,
  VETERINARY_APPLICATION_TYPES,
} from "./stockConstants";
import { CardDietItemDetails, StyledFormContainer } from "./styles";
import ButtonStandard from "../../../components/utils/button";
import SearchSupplier from "../../../components/utils/searchSupplier";
import SearchSupplement from "../../../components/utils/searchSupplement";
import SelectUnit from "../../../components/utils/selectUnit";
import SelectGroupItem from "../../../components/utils/selectGroupItem";
import SelectItemsGroup from "../../../components/utils/selectItemsGroup";
import SelectManufacturer from "../../../components/utils/selectManufacturer";
import useWindowSize from "../../../hooks/useWindowSize";
import RadioGroup from "antd/lib/radio/group";
import RadioButton from "../../../components/utils/radioButton";

const { Option } = Select;
const { TextArea } = Input;

const NUTRIENT_UNITS = [
  { label: "g/kg", value: "g_kg" },
  { label: "mg/kg", value: "mg_kg" },
  { label: "%", value: "percentage" },
];

const INITIAL_VALUES = {
  internalId: "",
  abbreviation: "",
  description: "",
  groupItemId: null,
  itemGroupId: null,
  manufacturerId: null,
  measurementUnitId: null,
  replenishmentPoint: "",
  currentCost: 0,
  hasVariant: false,
  batchControl: false,
  notes: "",
  accountingAccountId: null,
  dietItem: false,
  supplementType: "new",
  supplementId: null,
  product: "",
  supplementSupplierId: null,
  supplementGroupId: null,
  supplementSubGroupId: null,
  supplementPresentationId: null,
  cost: null,
  dryMatterPercentage: null,
  ndt: null,
  pb: null,
  ee: null,
  fdn: null,
  fda: null,
  ca: null,
  p: null,
  k: null,
  s: null,
  na: null,
  zinc: null,
  zincUnit: "g_kg",
  ndtUnit: "g_kg",
  pbUnit: "g_kg",
  eeUnit: "g_kg",
  fdnUnit: "percentage",
  fdaUnit: "g_kg",
  caUnit: "g_kg",
  pUnit: "g_kg",
  kUnit: "g_kg",
  sUnit: "g_kg",
  naUnit: "g_kg",
  veterinarySupplementType: "new",
  veterinarySupplementId: null,
  veterinarySupplement: false,
  type: "Vacina",
  name: "",
  supplierId: null,
  applicationGender: "Both",
  applicationType: "Fixo",
  applicationValue: "",
  applicationWeightValueParam: "",
  unitId: null,
  applicationCost: "",
};

const validationSchema = Yup.object({
  internalId: Yup.string().required("Campo obrigatório"),
  groupItemId: Yup.mixed().required("Campo obrigatório"),
  itemGroupId: Yup.mixed().required("Campo obrigatório"),
  measurementUnitId: Yup.mixed().required("Campo obrigatório"),
  supplementId: Yup.string().when(["dietItem", "supplementType"], {
    is: (dietItem, supplementType) => dietItem && supplementType === "existing",
    then: Yup.string().required("Campo obrigatório para item de dieta"),
    otherwise: Yup.string().notRequired().nullable(),
  }),
  product: Yup.string().when(["dietItem", "supplementType"], {
    is: (dietItem, supplementType) => dietItem && supplementType === "new",
    then: Yup.string().required("Campo obrigatório para item de dieta"),
  }),
  supplementGroupId: Yup.mixed().when(["dietItem", "supplementType"], {
    is: (dietItem, supplementType) => dietItem && supplementType === "new",
    then: Yup.mixed().required("Campo obrigatório para item de dieta"),
  }),
  supplementSubGroupId: Yup.mixed().when(["dietItem", "supplementType"], {
    is: (dietItem, supplementType) => dietItem && supplementType === "new",
    then: Yup.mixed().required("Campo obrigatório para item de dieta"),
  }),
  supplementPresentationId: Yup.mixed().when(["dietItem", "supplementType"], {
    is: (dietItem, supplementType) => dietItem && supplementType === "new",
    then: Yup.mixed().required("Campo obrigatório para item de dieta"),
  }),
  dryMatterPercentage: Yup.mixed().when(["dietItem", "supplementType"], {
    is: (dietItem, supplementType) => dietItem && supplementType === "new",
    then: Yup.mixed().required("Campo obrigatório para item de dieta"),
  }),
  ndt: Yup.mixed().when(["dietItem", "supplementType"], {
    is: (dietItem, supplementType) => dietItem && supplementType === "new",
    then: Yup.mixed().required("Campo obrigatório para item de dieta"),
  }),
  pb: Yup.mixed().when(["dietItem", "supplementType"], {
    is: (dietItem, supplementType) => dietItem && supplementType === "new",
    then: Yup.mixed().required("Campo obrigatório para item de dieta"),
  }),
  veterinarySupplementId: Yup.string().when(
    ["veterinarySupplement", "veterinarySupplementType"],
    {
      is: (veterinarySupplement, veterinarySupplementType) =>
        veterinarySupplement && veterinarySupplementType === "existing",
      then: Yup.string().required("Campo obrigatório para item veterinário"),
      otherwise: Yup.string().notRequired().nullable(),
    },
  ),
  type: Yup.string().when(
    ["veterinarySupplement", "veterinarySupplementType"],
    {
      is: (veterinarySupplement, veterinarySupplementType) =>
        veterinarySupplement && veterinarySupplementType === "new",
      then: Yup.string().required("Campo obrigatório para item veterinário"),
    },
  ),
  name: Yup.string()
    .notRequired()
    .nullable()
    .when(["veterinarySupplement", "veterinarySupplementType"], {
      is: (veterinarySupplement, veterinarySupplementType) =>
        veterinarySupplement && veterinarySupplementType === "new",
      then: Yup.string().required("Campo obrigatório para item veterinário"),
    }),
  supplementVeterinarySupplierId: Yup.string()
    .notRequired()
    .nullable()
    .when(["veterinarySupplement", "veterinarySupplementType"], {
      is: (veterinarySupplement, veterinarySupplementType) =>
        veterinarySupplement && veterinarySupplementType === "new",
      then: Yup.string().required("Campo obrigatório para item veterinário"),
    }),
  applicationGender: Yup.string()
    .notRequired()
    .nullable()
    .when(["veterinarySupplement", "veterinarySupplementType"], {
      is: (veterinarySupplement, veterinarySupplementType) =>
        veterinarySupplement && veterinarySupplementType === "new",
      then: Yup.string().required("Campo obrigatório para item veterinário"),
    }),
  applicationType: Yup.string()
    .notRequired()
    .nullable()
    .when(["veterinarySupplement", "veterinarySupplementType"], {
      is: (veterinarySupplement, veterinarySupplementType) =>
        veterinarySupplement && veterinarySupplementType === "new",
      then: Yup.string().required("Campo obrigatório para item veterinário"),
    }),
  applicationValue: Yup.number().when(
    ["veterinarySupplement", "veterinarySupplementType"],
    {
      is: (veterinarySupplement, veterinarySupplementType) =>
        veterinarySupplement && veterinarySupplementType === "new",
      then: Yup.number()
        .min(0.1, "Campo obrigatório para item veterinário")
        .max(1000)
        .required("Campo obrigatório para item veterinário"),
    },
  ),
  applicationWeightValueParam: Yup.number()
    .notRequired()
    .nullable()
    .when(
      ["veterinarySupplement", "applicationType", "veterinarySupplementType"],
      {
        is: (veterinarySupplement, applicationType, veterinarySupplementType) =>
          veterinarySupplement &&
          veterinarySupplementType === "new" &&
          applicationType !== "Fixo",
        then: Yup.number()
          .min(0.1, "Campo obrigatório para item veterinário")
          .max(1000)
          .required("Campo obrigatório para item veterinário"),
      },
    ),
  unitId: Yup.string()
    .notRequired()
    .nullable()
    .when(["veterinarySupplement", "veterinarySupplementType"], {
      is: (veterinarySupplement, veterinarySupplementType) =>
        veterinarySupplement && veterinarySupplementType === "new",
      then: Yup.string().required("Campo obrigatório para item veterinário"),
    }),
  applicationCost: Yup.number().when(
    ["veterinarySupplement", "veterinarySupplementType"],
    {
      is: (veterinarySupplement, veterinarySupplementType) =>
        veterinarySupplement && veterinarySupplementType === "new",
      then: Yup.number()
        .moreThan(0, "Campo obrigatório para item veterinário")
        .max(1000)
        .required("Campo obrigatório para item veterinário"),
    },
  ),
});

const selectNutrientUnit = (field, value, setFieldValue) => (
  <Select
    value={value || undefined}
    onChange={(valueSelected) => setFieldValue(field, valueSelected)}
  >
    {NUTRIENT_UNITS.map((unit) => (
      <Option key={unit.value} value={unit.value}>
        {unit.label}
      </Option>
    ))}
  </Select>
);

const NutritionField = ({
  label,
  name,
  values,
  setFieldValue,
  placeholder,
  isRequired = false,
  hasError,
}) => {
  const unitName = `${name}Unit`;
  return (
    <Form.Item
      label={
        <span className={hasError(name) ? "label-error" : ""}>
          {`${label}${isRequired ? "*" : ""}`}
        </span>
      }
      validateStatus={hasError(name) ? "error" : ""}
    >
      <NumberFormat
        customInput={Input}
        placeholder={placeholder}
        addonAfter={selectNutrientUnit(
          unitName,
          values[unitName] ?? "percentage",
          setFieldValue,
        )}
        value={values[name] !== null ? values[name] : ""}
        decimalScale={4}
        decimalSeparator=","
        thousandSeparator="."
        fixedDecimalScale={true}
        allowLeadingZeros={true}
        onValueChange={({ floatValue }) =>
          setFieldValue(name, floatValue !== undefined ? floatValue : null)
        }
      />
    </Form.Item>
  );
};

const StockItemNewModal = () => {
  const size = useWindowSize();
  const formikRef = useRef(null);
  const { translation } = useSelector((state) => state.app);
  const t = translation?.stockItem || {};
  const tf = t.form || {};
  const defaultPlaceholder =
    translation?.defaultPlaceholder || "Informe um valor";

  const {
    isModalVisible,
    isLoadingEdit,
    handleSaveItem,
    handleCloseModal,
    supplierSearchText,
    setSupplierSearchText,
    textSearchSupplement,
    setTextSearchSupplement,
    veterinarySupplements,
  } = useStockItemContext();

  const handleClearNutritionalData = useCallback((setFieldValue) => {
    setFieldValue("pb", "");
    setFieldValue("ndt", "");
    setFieldValue("dryMatterPercentage", "");
    setFieldValue("nitrogen", "");
    setFieldValue("phosphorus", "");
    setFieldValue("zinc", "");
    setFieldValue("sodium", "");
    setFieldValue("calcium", "");
    setFieldValue("sulfur", "");
    setFieldValue("proportion", "");
    setFieldValue("additiveEffect", "");
    setFieldValue("additiveEffectUnit", "");
    setFieldValue("fda", "");
    setFieldValue("lignin", "");
    setFieldValue("ee", "");
    setFieldValue("supplementGroupId", null);
    setFieldValue("supplementSubGroupId", null);
    setFieldValue("supplementPresentationId", null);
    setFieldValue("fdn", "");
    setFieldValue("pdr", "");
    setFieldValue("pndr", "");
    setFieldValue("em", "");
    setFieldValue("fef", "");
    setFieldValue("fdnfe", "");
  }, []);

  const handleDeselectSupplement = useCallback(
    (setFieldValue) => {
      Modal.confirm({
        centered: true,
        content: translation.supplement.confirmClearNutritionalData,
        cancelText: translation.buttons.confirm,
        okText: translation.buttons.no,
        zIndex: 1012,
        onCancel: () => {
          setFieldValue("originalSupplementId", null);
          handleClearNutritionalData(setFieldValue);
        },
        onOk: () => {
          setFieldValue("originalSupplementId", null);
        },
      });
    },
    [
      handleClearNutritionalData,
      translation.buttons.confirm,
      translation.buttons.no,
      translation.supplement.confirmClearNutritionalData,
    ],
  );

  const handleSelectSupplement = useCallback((supplement, setFieldValue) => {
    const lastVersion = supplement.versions[supplement.versions.length - 1];

    setFieldValue("pb", lastVersion.cp);
    setFieldValue("pbUnit", lastVersion.cpUnit);
    setFieldValue("ndt", lastVersion.tdn);
    setFieldValue("ndtUnit", lastVersion.tdnUnit);
    setFieldValue("dryMatterPercentage", lastVersion.dryMatterPercentage);
    setFieldValue("nitrogen", lastVersion.nitrogen);
    setFieldValue("phosphorus", lastVersion.phosphorus);
    setFieldValue("zinc", lastVersion.zinc);
    setFieldValue("sodium", lastVersion.sodium);
    setFieldValue("calcium", lastVersion.calcium);
    setFieldValue("sulfur", lastVersion.sulfur);
    setFieldValue("potassium", lastVersion.potassium);
    setFieldValue("potassiumUnit", lastVersion.potassiumUnit);
    setFieldValue("nitrogenUnit", lastVersion.nitrogenUnit);
    setFieldValue("calciumUnit", lastVersion.calciumUnit);
    setFieldValue("phosphorusUnit", lastVersion.phosphorusUnit);
    setFieldValue("zincUnit", lastVersion.zincUnit);
    setFieldValue("sodiumUnit", lastVersion.sodiumUnit);
    setFieldValue("sulfurUnit", lastVersion.sulfurUnit);
    setFieldValue("proportion", lastVersion.proportion);
    setFieldValue("additiveEffect", lastVersion.additiveEffect);
    setFieldValue("additiveEffectUnit", lastVersion.additiveEffectUnit);
    setFieldValue("fda", lastVersion.fda);
    setFieldValue("lignin", lastVersion.lignin);
    setFieldValue("ee", lastVersion.ee);
    setFieldValue("supplementGroupId", supplement.supplementGroupId);
    setFieldValue("supplementSubGroupId", supplement.supplementSubGroupId);
    setFieldValue(
      "supplementPresentationId",
      supplement.supplementPresentationId,
    );
    setFieldValue("fdn", lastVersion.fdn);
    setFieldValue("pdr", lastVersion.pdr);
    setFieldValue("pndr", lastVersion.pndr);
    setFieldValue("em", lastVersion.em);
    setFieldValue("fef", lastVersion.fef);
    setFieldValue("fdnfe", lastVersion.fdnfe);
  }, []);

  useEffect(() => {
    if (isModalVisible && formikRef.current) {
      formikRef.current.resetForm(INITIAL_VALUES);
    }
  }, [isModalVisible, setSupplierSearchText, setTextSearchSupplement]);

  return (
    <Formik
      ref={formikRef}
      enableReinitialize
      initialValues={INITIAL_VALUES}
      validationSchema={validationSchema}
      onSubmit={(values, { resetForm }) => {
        handleSaveItem(values);
      }}
    >
      {({
        values,
        errors,
        submitCount,
        setFieldValue,
        handleSubmit,
        resetForm,
      }) => {
        const hasError = (field) => submitCount > 0 && errors[field];

        const handleCancel = () => {
          resetForm();
          handleCloseModal();
        };

        return (
          <Modal
            title={t.newItem || "Novo Item de Estoque"}
            visible={isModalVisible}
            width={size.width < 768 ? "100%" : size.width * 0.8}
            maskClosable={false}
            footer={null}
            onCancel={handleCancel}
            zIndex={1010}
            destroyOnClose
          >
            <Spin spinning={isLoadingEdit}>
              <StyledFormContainer>
                <Form layout="vertical">
                  <Row type="flex" gutter={16}>
                    <Col span={8}>
                      <Form.Item
                        required
                        label={
                          <span
                            className={
                              hasError("internalId") ? "label-error" : ""
                            }
                          >
                            {tf.internalId || "Identificação"}*
                          </span>
                        }
                        validateStatus={hasError("internalId") ? "error" : ""}
                        help={hasError("internalId") ? errors.internalId : null}
                      >
                        <Input
                          placeholder={defaultPlaceholder}
                          value={values.internalId}
                          onChange={(e) => {
                            setFieldValue("internalId", e.target.value);
                            if (values.dietItem) {
                              setFieldValue("product", e.target.value);
                            }
                            if (values.veterinarySupplement) {
                              setFieldValue("name", e.target.value);
                            }
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        required
                        label={
                          <span
                            className={
                              hasError("abbreviation") ? "label-error" : ""
                            }
                          >
                            {tf.abbreviation || "Abreviação"}
                          </span>
                        }
                        validateStatus={hasError("abbreviation") ? "error" : ""}
                        help={
                          hasError("abbreviation") ? errors.abbreviation : null
                        }
                      >
                        <Input
                          placeholder={defaultPlaceholder}
                          value={values.abbreviation}
                          onChange={(e) =>
                            setFieldValue("abbreviation", e.target.value)
                          }
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        required
                        label={
                          <span
                            className={
                              hasError("description") ? "label-error" : ""
                            }
                          >
                            {tf.description || "Descrição"}
                          </span>
                        }
                        validateStatus={hasError("description") ? "error" : ""}
                        help={
                          hasError("description") ? errors.description : null
                        }
                      >
                        <Input
                          placeholder={defaultPlaceholder}
                          value={values.description}
                          onChange={(e) =>
                            setFieldValue("description", e.target.value)
                          }
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={8}>
                      <Form.Item
                        label={
                          <span
                            className={
                              hasError("groupItemId") ? "label-error" : ""
                            }
                          >
                            {tf.group || "Grupo"}*
                          </span>
                        }
                        validateStatus={hasError("groupItemId") ? "error" : ""}
                        help={
                          hasError("groupItemId") ? errors.groupItemId : null
                        }
                      >
                        <SelectGroupItem
                          value={values.groupItemId || undefined}
                          onChange={(val) => {
                            setFieldValue("groupItemId", val);
                            setFieldValue("itemGroupId", null);
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        required
                        label={
                          <span
                            className={
                              hasError("itemGroupId") ? "label-error" : ""
                            }
                          >
                            {tf.subgroup || "Subgrupo"}*
                          </span>
                        }
                        validateStatus={hasError("itemGroupId") ? "error" : ""}
                        help={
                          hasError("itemGroupId") ? errors.itemGroupId : null
                        }
                      >
                        <SelectItemsGroup
                          groupItemId={values.groupItemId}
                          value={values.itemGroupId || undefined}
                          onChange={(val) => setFieldValue("itemGroupId", val)}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        required
                        label={
                          <span
                            className={
                              hasError("measurementUnitId") ? "label-error" : ""
                            }
                          >
                            {`${tf.measurementUnit}*` || "Unidade Medida*"}
                          </span>
                        }
                        validateStatus={
                          hasError("measurementUnitId") ? "error" : ""
                        }
                        help={
                          hasError("measurementUnitId")
                            ? errors.measurementUnitId
                            : null
                        }
                      >
                        <SelectUnit
                          value={values.measurementUnitId || undefined}
                          onChange={(val) =>
                            setFieldValue("measurementUnitId", val)
                          }
                          allowClear
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={8}>
                      <Form.Item
                        required
                        label={
                          <span
                            className={
                              hasError("manufacturerId") ? "label-error" : ""
                            }
                          >
                            {tf.manufacturer || "Fabricante"}
                          </span>
                        }
                      >
                        <SelectManufacturer
                          value={values.manufacturerId}
                          onChange={(val) =>
                            setFieldValue("manufacturerId", val)
                          }
                          onDeselect={() => setFieldValue("manufacturerId", "")}
                          placeholder={defaultPlaceholder}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label={tf.replenishmentPoint || "Ponto Reposição"}
                      >
                        <NumberFormat
                          customInput={Input}
                          placeholder={defaultPlaceholder}
                          value={values.replenishmentPoint}
                          decimalScale={4}
                          decimalSeparator=","
                          thousandSeparator="."
                          allowLeadingZeros
                          onValueChange={({ floatValue }) =>
                            setFieldValue(
                              "replenishmentPoint",
                              floatValue !== undefined ? floatValue : 0,
                            )
                          }
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  {/* Has Variant | Crl. Lote | Item de Dieta | Item Veterinário */}
                  <Row gutter={16}>
                    <Col span={6}>
                      <Form.Item label={tf.variantControl || "Ctrl Variante"}>
                        <Switch
                          checked={values.hasVariant}
                          onChange={(val) => setFieldValue("hasVariant", val)}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item label={tf.batchControl || "Ctrl Lote"}>
                        <Switch
                          checked={values.batchControl}
                          onChange={(val) => setFieldValue("batchControl", val)}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item label={tf.dietItem || "Item de Dieta"}>
                        <Switch
                          checked={values.dietItem}
                          onChange={(val) => {
                            setFieldValue("dietItem", val);
                            if (val) {
                              setFieldValue("product", values.internalId);
                              setFieldValue("veterinarySupplement", false);
                            } else {
                              setFieldValue("product", null);
                            }
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item
                        label={tf.veterinarySupplement || "Item Veterinário"}
                      >
                        <Switch
                          checked={values.veterinarySupplement}
                          onChange={(val) => {
                            setFieldValue("veterinarySupplement", val);
                            if (val) {
                              setFieldValue("dietItem", false);
                              setFieldValue("name", values.internalId);
                            } else {
                              setFieldValue("name", null);
                            }
                          }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  {values.dietItem && (
                    <CardDietItemDetails>
                      <Row gutter={16} style={{ marginBottom: 16 }}>
                        <Col span={24}>
                          <strong>
                            {tf.nutritionalDetails || "DADOS NUTRICIONAIS"}
                          </strong>
                        </Col>
                      </Row>
                      <Row
                        type="flex"
                        gutter={8}
                        style={{ marginBottom: 8, marginTop: 8 }}
                      >
                        <Col span={24}>
                          <RadioGroup
                            value={values.supplementType || "new"}
                            onChange={(e) => {
                              setFieldValue("supplementType", e.target.value);
                              if (e.target.value === "new") {
                                setFieldValue("supplementId", null);
                                setTextSearchSupplement("");
                              }
                            }}
                          >
                            <RadioButton value="new">
                              {tf.supplementTypeNew || "Novo"}
                            </RadioButton>
                            <RadioButton value="existing">
                              {tf.supplementTypeExisting || "Já existente"}
                            </RadioButton>
                          </RadioGroup>
                        </Col>
                      </Row>
                      {values.supplementType === "existing" && (
                        <Row type="flex" align="middle">
                          <Col span={24}>
                            <Form.Item
                              label={
                                `${
                                  translation.supplement.form.originalSupplement
                                }*` || "Matéria/Suplemento Original*"
                              }
                              className={
                                hasError("supplementId") ? "label-error" : ""
                              }
                            >
                              <SearchSupplement
                                textSearch={textSearchSupplement}
                                setTextSearch={setTextSearchSupplement}
                                value={values.supplementId || undefined}
                                handleChange={(value, supplementSelected) => {
                                  setFieldValue("supplementId", value);
                                }}
                                showDeselect
                                handleDeselect={(supplement) => {
                                  setFieldValue("supplementId", null);
                                }}
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                      )}
                      {values.supplementType === "new" && (
                        <>
                          <Row type="flex" align="middle">
                            <Col span={24}>
                              <Form.Item
                                label={
                                  tf.originalSupplement ||
                                  "Matéria Prima/Suplemento Original"
                                }
                              >
                                <SearchSupplement
                                  textSearch={textSearchSupplement}
                                  setTextSearch={setTextSearchSupplement}
                                  value={
                                    values.originalSupplementId || undefined
                                  }
                                  handleChange={(value, supplementSelected) => {
                                    setFieldValue(
                                      "originalSupplementId",
                                      value,
                                    );
                                    if (value) {
                                      handleSelectSupplement(
                                        supplementSelected,
                                        setFieldValue,
                                      );
                                    }
                                  }}
                                  showDeselect
                                  handleDeselect={(supplement) =>
                                    handleDeselectSupplement(setFieldValue)
                                  }
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                          <Row type="flex" gutter={8}>
                            <Col span={12}>
                              <Form.Item
                                label={
                                  <span
                                    className={
                                      hasError("product") ? "label-error" : ""
                                    }
                                  >
                                    {tf.product || "Produto"}*
                                  </span>
                                }
                                validateStatus={
                                  hasError("product") ? "error" : ""
                                }
                                help={
                                  hasError("product") ? errors.product : null
                                }
                              >
                                <Input
                                  placeholder={defaultPlaceholder}
                                  value={values.product}
                                  disabled
                                />
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item
                                label={
                                  <span
                                    className={
                                      hasError("supplementSupplierId")
                                        ? "label-error"
                                        : ""
                                    }
                                  >
                                    {`${tf.supplementSupplierId}*` ||
                                      "Fornecedor*"}
                                  </span>
                                }
                                validateStatus={
                                  hasError("supplementSupplierId")
                                    ? "error"
                                    : ""
                                }
                                help={
                                  hasError("supplementSupplierId")
                                    ? errors.supplementSupplierId
                                    : null
                                }
                              >
                                <SearchSupplier
                                  textSearch={supplierSearchText}
                                  setTextSearch={setSupplierSearchText}
                                  value={values.supplementSupplierId}
                                  handleChange={(val) =>
                                    setFieldValue("supplementSupplierId", val)
                                  }
                                  handleDeselect={() => {
                                    setFieldValue("supplementSupplierId", null);
                                  }}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                          <Row gutter={8}>
                            <Col span={8}>
                              <Form.Item
                                required
                                label={
                                  <span
                                    className={
                                      hasError("supplementGroupId")
                                        ? "label-error"
                                        : ""
                                    }
                                  >
                                    {tf.supplementGroup ||
                                      "Grupo do Suplemento"}
                                    *
                                  </span>
                                }
                                validateStatus={
                                  hasError("supplementGroupId") ? "error" : ""
                                }
                                help={
                                  hasError("supplementGroupId")
                                    ? errors.supplementGroupId
                                    : null
                                }
                              >
                                <Select
                                  value={values.supplementGroupId || undefined}
                                  onChange={(val) => {
                                    setFieldValue("supplementGroupId", val);
                                    setFieldValue("supplementSubGroupId", null);
                                  }}
                                  allowClear
                                  placeholder="Selecione"
                                >
                                  {SUPPLEMENT_GROUPS.map((g) => (
                                    <Option key={g.id} value={g.id}>
                                      {g.name}
                                    </Option>
                                  ))}
                                </Select>
                              </Form.Item>
                            </Col>
                            <Col span={8}>
                              <Form.Item
                                required
                                label={
                                  <span
                                    className={
                                      hasError("supplementSubGroupId")
                                        ? "label-error"
                                        : ""
                                    }
                                  >
                                    {tf.supplementSubGroup ||
                                      "Subgrupo do Suplemento"}
                                    *
                                  </span>
                                }
                                validateStatus={
                                  hasError("supplementSubGroupId")
                                    ? "error"
                                    : ""
                                }
                                help={
                                  hasError("supplementSubGroupId")
                                    ? errors.supplementSubGroupId
                                    : null
                                }
                              >
                                <Select
                                  value={
                                    values.supplementSubGroupId || undefined
                                  }
                                  onChange={(val) =>
                                    setFieldValue("supplementSubGroupId", val)
                                  }
                                  allowClear
                                  placeholder="Selecione"
                                >
                                  {SUPPLEMENT_SUB_GROUPS.filter(
                                    (sg) =>
                                      values.supplementGroupId &&
                                      sg.supplement_group_id ===
                                        values.supplementGroupId,
                                  ).map((sg) => (
                                    <Option key={sg.id} value={sg.id}>
                                      {sg.name}
                                    </Option>
                                  ))}
                                </Select>
                              </Form.Item>
                            </Col>
                            <Col span={8}>
                              <Form.Item
                                required
                                label={
                                  <span
                                    className={
                                      hasError("supplementPresentationId")
                                        ? "label-error"
                                        : ""
                                    }
                                  >
                                    {tf.supplementPresentation ||
                                      "Apresentação"}
                                    *
                                  </span>
                                }
                                validateStatus={
                                  hasError("supplementPresentationId")
                                    ? "error"
                                    : ""
                                }
                                help={
                                  hasError("supplementPresentationId")
                                    ? errors.supplementPresentationId
                                    : null
                                }
                              >
                                <Select
                                  value={
                                    values.supplementPresentationId || undefined
                                  }
                                  onChange={(val) =>
                                    setFieldValue(
                                      "supplementPresentationId",
                                      val,
                                    )
                                  }
                                  allowClear
                                  placeholder="Selecione"
                                >
                                  {SUPPLEMENT_PRESENTATION.map((p) => (
                                    <Option key={p.id} value={p.id}>
                                      {p.name}
                                    </Option>
                                  ))}
                                </Select>
                              </Form.Item>
                            </Col>
                          </Row>
                          <Row gutter={8}>
                            {[
                              {
                                name: "ndt",
                                label:
                                  tf.ndt || "NDT: Nutrientes Digest. Totais",
                                required: true,
                              },
                              {
                                name: "pb",
                                label: tf.pb || "PB: Proteína Bruta",
                                required: true,
                              },
                              {
                                name: "dryMatterPercentage",
                                label:
                                  tf.dryMatterPercentage || "MS: Matéria Seca",
                                required: true,
                              },
                              { name: "ee", label: tf.ee || "Extrato Etéreo" },
                              { name: "fdn", label: tf.fdn || "FDN" },
                              { name: "fda", label: tf.fda || "FDA" },
                              { name: "ca", label: tf.ca || "Cálcio" },
                              { name: "p", label: tf.p || "Fósforo" },
                              { name: "k", label: tf.k || "Potássio" },
                              { name: "s", label: tf.s || "Enxofre" },
                              { name: "na", label: tf.na || "Sódio" },
                              { name: "zinc", label: tf.zinc || "Zinco" },
                            ].map((field) => {
                              if (
                                field.name === "dryMatterPercentage" ||
                                field.name === "ee"
                              ) {
                                return (
                                  <Col key={field.name} span={8}>
                                    <Form.Item
                                      label={
                                        <span
                                          className={
                                            hasError(field.name)
                                              ? "label-error"
                                              : ""
                                          }
                                        >
                                          {`${field.label}${field.required ? "*" : ""}`}
                                        </span>
                                      }
                                      validateStatus={
                                        hasError(field.name) ? "error" : ""
                                      }
                                    >
                                      <NumberFormat
                                        customInput={Input}
                                        placeholder={defaultPlaceholder}
                                        addonAfter="%MS"
                                        value={
                                          values[field.name] !== null
                                            ? values[field.name]
                                            : ""
                                        }
                                        decimalScale={4}
                                        decimalSeparator=","
                                        thousandSeparator="."
                                        fixedDecimalScale={false}
                                        onValueChange={({ floatValue }) =>
                                          setFieldValue(
                                            field.name,
                                            floatValue !== undefined
                                              ? floatValue
                                              : null,
                                          )
                                        }
                                      />
                                    </Form.Item>
                                  </Col>
                                );
                              }
                              if (field.name === "fda") {
                                return (
                                  <Col key={field.name} span={8}>
                                    <Form.Item
                                      label={`${field.label}${field.required ? "*" : ""}`}
                                      colon={false}
                                    >
                                      <NumberFormat
                                        customInput={Input}
                                        placeholder={defaultPlaceholder}
                                        addonAfter="g/kg"
                                        value={
                                          values[field.name] !== null
                                            ? values[field.name]
                                            : ""
                                        }
                                        decimalScale={4}
                                        decimalSeparator=","
                                        thousandSeparator="."
                                        fixedDecimalScale={false}
                                        onValueChange={({ floatValue }) =>
                                          setFieldValue(
                                            field.name,
                                            floatValue !== undefined
                                              ? floatValue
                                              : null,
                                          )
                                        }
                                      />
                                    </Form.Item>
                                  </Col>
                                );
                              }
                              if (field.name === "fdn") {
                                return (
                                  <Col key={field.name} span={8}>
                                    <Form.Item
                                      label={`${field.label}${field.required ? "*" : ""}`}
                                      colon={false}
                                    >
                                      <NumberFormat
                                        customInput={Input}
                                        placeholder={defaultPlaceholder}
                                        addonAfter="%"
                                        value={
                                          values[field.name] !== null
                                            ? values[field.name]
                                            : ""
                                        }
                                        decimalScale={4}
                                        decimalSeparator=","
                                        thousandSeparator="."
                                        fixedDecimalScale={false}
                                        onValueChange={({ floatValue }) =>
                                          setFieldValue(
                                            field.name,
                                            floatValue !== undefined
                                              ? floatValue
                                              : null,
                                          )
                                        }
                                      />
                                    </Form.Item>
                                  </Col>
                                );
                              }
                              return (
                                <Col key={field.name} span={8}>
                                  <NutritionField
                                    label={field.label}
                                    name={field.name}
                                    isRequired={field.required || false}
                                    values={values}
                                    setFieldValue={setFieldValue}
                                    placeholder={defaultPlaceholder}
                                    hasError={hasError}
                                  />
                                </Col>
                              );
                            })}
                          </Row>
                        </>
                      )}
                    </CardDietItemDetails>
                  )}
                  {values.veterinarySupplement && (
                    <CardDietItemDetails>
                      <Row gutter={16} style={{ marginBottom: 16 }}>
                        <Col span={24}>
                          <strong>
                            {tf.veterinaryDetails ||
                              "DADOS DO SUPLEMENTO VETERINÁRIO"}
                          </strong>
                        </Col>
                      </Row>
                      <Row
                        type="flex"
                        gutter={8}
                        style={{ marginBottom: 8, marginTop: 8 }}
                      >
                        <Col span={24}>
                          <RadioGroup
                            value={values.veterinarySupplementType || "new"}
                            onChange={(e) => {
                              setFieldValue(
                                "veterinarySupplementType",
                                e.target.value,
                              );
                              if (e.target.value === "new") {
                                setFieldValue("veterinarySupplementId", null);
                              }
                            }}
                          >
                            <RadioButton value="new">
                              {tf.veterinarySupplementTypeNew || "Novo"}
                            </RadioButton>
                            <RadioButton value="existing">
                              {tf.veterinarySupplementTypeExisting ||
                                "Já existente"}
                            </RadioButton>
                          </RadioGroup>
                        </Col>
                      </Row>
                      {values.veterinarySupplementType === "existing" && (
                        <Row type="flex">
                          <Col span={12}>
                            <Form.Item
                              label={
                                `${tf.veterinarySupplementId}*` ||
                                "Suplemento Veterinário*"
                              }
                              className={
                                hasError("supplementId") ? "label-error" : ""
                              }
                            >
                              <Select
                                value={
                                  values.veterinarySupplementId ?? undefined
                                }
                                placeholder={
                                  translation.defaultSelectPlaceholder
                                }
                                style={{
                                  width: "100%",
                                }}
                                onChange={(value) => {
                                  setFieldValue(
                                    "veterinarySupplementId",
                                    value,
                                  );
                                }}
                              >
                                {veterinarySupplements.map(
                                  (veterinarySupplement) => (
                                    <Select.Option
                                      key={veterinarySupplement.id}
                                      value={veterinarySupplement.id}
                                    >
                                      {veterinarySupplement.name}
                                    </Select.Option>
                                  ),
                                )}
                              </Select>
                            </Form.Item>
                          </Col>
                        </Row>
                      )}
                      {values.veterinarySupplementType === "new" && (
                        <>
                          <Row gutter={8}>
                            <Col span={12}>
                              <Form.Item
                                required
                                label={
                                  <span
                                    className={
                                      hasError("type") ? "label-error" : ""
                                    }
                                  >
                                    {tf.type || "Tipo"}*
                                  </span>
                                }
                                validateStatus={hasError("type") ? "error" : ""}
                                help={hasError("type") ? errors.type : null}
                              >
                                <Select
                                  value={values.type || undefined}
                                  onChange={(val) => setFieldValue("type", val)}
                                  allowClear
                                  placeholder="Selecione"
                                >
                                  {VETERINARY_TYPES.map((option) => (
                                    <Option key={option.key} value={option.key}>
                                      {option.name}
                                    </Option>
                                  ))}
                                </Select>
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item
                                required
                                label={
                                  <span
                                    className={
                                      hasError("name") ? "label-error" : ""
                                    }
                                  >
                                    {tf.name || "Nome Comercial"}*
                                  </span>
                                }
                                validateStatus={hasError("name") ? "error" : ""}
                                help={hasError("name") ? errors.name : null}
                              >
                                <Input
                                  maxLength={50}
                                  placeholder={defaultPlaceholder}
                                  value={values.name}
                                  disabled
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                          <Row gutter={8}>
                            <Col span={12}>
                              <Form.Item
                                required
                                label={
                                  <span
                                    className={
                                      hasError("supplementVeterinarySupplierId")
                                        ? "label-error"
                                        : ""
                                    }
                                  >
                                    {tf.supplierId || "Fornecedor"}*
                                  </span>
                                }
                                validateStatus={
                                  hasError("supplementVeterinarySupplierId")
                                    ? "error"
                                    : ""
                                }
                                help={
                                  hasError("supplementVeterinarySupplierId")
                                    ? errors.supplementVeterinarySupplierId
                                    : null
                                }
                              >
                                <SearchSupplier
                                  textSearch={supplierSearchText}
                                  setTextSearch={setSupplierSearchText}
                                  value={values.supplementVeterinarySupplierId}
                                  handleChange={(val) =>
                                    setFieldValue(
                                      "supplementVeterinarySupplierId",
                                      val,
                                    )
                                  }
                                  handleDeselect={() => {
                                    setFieldValue(
                                      "supplementVeterinarySupplierId",
                                      null,
                                    );
                                  }}
                                />
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item
                                required
                                label={
                                  <span
                                    className={
                                      hasError("applicationGender")
                                        ? "label-error"
                                        : ""
                                    }
                                  >
                                    {tf.applicationGender || "Sexo"}*
                                  </span>
                                }
                                validateStatus={
                                  hasError("applicationGender") ? "error" : ""
                                }
                                help={
                                  hasError("applicationGender")
                                    ? errors.applicationGender
                                    : null
                                }
                              >
                                <Select
                                  value={values.applicationGender || undefined}
                                  onChange={(val) =>
                                    setFieldValue("applicationGender", val)
                                  }
                                  allowClear
                                  placeholder="Selecione"
                                >
                                  {VETERINARY_APPLICATION_GENDER_TYPES.map(
                                    (option) => (
                                      <Option
                                        key={option.key}
                                        value={option.key}
                                      >
                                        {option.name}
                                      </Option>
                                    ),
                                  )}
                                </Select>
                              </Form.Item>
                            </Col>
                          </Row>
                          <Row gutter={8}>
                            <Col span={8}>
                              <Form.Item
                                required
                                label={
                                  <span
                                    className={
                                      hasError("applicationType")
                                        ? "label-error"
                                        : ""
                                    }
                                  >
                                    {tf.applicationType || "Forma de Aplicação"}
                                    *
                                  </span>
                                }
                                validateStatus={
                                  hasError("applicationType") ? "error" : ""
                                }
                                help={
                                  hasError("applicationType")
                                    ? errors.applicationType
                                    : null
                                }
                              >
                                <Select
                                  value={values.applicationType || undefined}
                                  onChange={(val) =>
                                    setFieldValue("applicationType", val)
                                  }
                                  allowClear
                                  placeholder="Selecione"
                                >
                                  {VETERINARY_APPLICATION_TYPES.map(
                                    (option) => (
                                      <Option
                                        key={option.key}
                                        value={option.key}
                                      >
                                        {option.name}
                                      </Option>
                                    ),
                                  )}
                                </Select>
                              </Form.Item>
                            </Col>
                            <Col span={8}>
                              <Form.Item
                                required
                                label={
                                  <span
                                    className={
                                      hasError("applicationValue")
                                        ? "label-error"
                                        : ""
                                    }
                                  >
                                    {tf.applicationValue || "Qtd. de Aplicação"}
                                    *
                                  </span>
                                }
                                validateStatus={
                                  hasError("applicationValue") ? "error" : ""
                                }
                                help={
                                  hasError("applicationValue")
                                    ? errors.applicationValue
                                    : null
                                }
                              >
                                <NumberFormat
                                  customInput={Input}
                                  placeholder={defaultPlaceholder}
                                  value={values.applicationValue}
                                  allowNegative={false}
                                  decimalScale={1}
                                  decimalSeparator=","
                                  thousandSeparator="."
                                  fixedDecimalScale={false}
                                  onValueChange={({ floatValue }) =>
                                    setFieldValue(
                                      "applicationValue",
                                      floatValue !== undefined
                                        ? floatValue
                                        : "",
                                    )
                                  }
                                />
                              </Form.Item>
                            </Col>
                            {values.applicationType !== "Fixo" && (
                              <Col span={8}>
                                <Form.Item
                                  required
                                  label={
                                    <span
                                      className={
                                        hasError("applicationWeightValueParam")
                                          ? "label-error"
                                          : ""
                                      }
                                    >
                                      {tf.applicationWeightValueParam || "Peso"}
                                      *
                                    </span>
                                  }
                                  validateStatus={
                                    hasError("applicationWeightValueParam")
                                      ? "error"
                                      : ""
                                  }
                                  help={
                                    hasError("applicationWeightValueParam")
                                      ? errors.applicationWeightValueParam
                                      : null
                                  }
                                >
                                  <NumberFormat
                                    customInput={Input}
                                    addonAfter="kg"
                                    placeholder={defaultPlaceholder}
                                    value={values.applicationWeightValueParam}
                                    allowNegative={false}
                                    decimalScale={2}
                                    decimalSeparator=","
                                    thousandSeparator="."
                                    fixedDecimalScale={false}
                                    onValueChange={({ floatValue }) =>
                                      setFieldValue(
                                        "applicationWeightValueParam",
                                        floatValue !== undefined
                                          ? floatValue
                                          : "",
                                      )
                                    }
                                  />
                                </Form.Item>
                              </Col>
                            )}
                          </Row>
                          <Row gutter={8}>
                            <Col span={12}>
                              <Form.Item
                                required
                                label={
                                  <span
                                    className={
                                      hasError("unitId") ? "label-error" : ""
                                    }
                                  >
                                    {tf.unitId || "Unidade de uso"}*
                                  </span>
                                }
                                validateStatus={
                                  hasError("unitId") ? "error" : ""
                                }
                                help={hasError("unitId") ? errors.unitId : null}
                              >
                                <SelectUnit
                                  value={values.unitId || undefined}
                                  onChange={(val) =>
                                    setFieldValue("unitId", val)
                                  }
                                  allowClear
                                />
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item
                                required
                                label={
                                  <span
                                    className={
                                      hasError("applicationCost")
                                        ? "label-error"
                                        : ""
                                    }
                                  >
                                    {tf.applicationCost || "Custo Aplicação"}*
                                  </span>
                                }
                                validateStatus={
                                  hasError("applicationCost") ? "error" : ""
                                }
                                help={
                                  hasError("applicationCost")
                                    ? errors.applicationCost
                                    : null
                                }
                              >
                                <NumberFormat
                                  customInput={Input}
                                  addonBefore="R$"
                                  placeholder={defaultPlaceholder}
                                  value={values.applicationCost}
                                  allowNegative={false}
                                  decimalScale={5}
                                  decimalSeparator=","
                                  thousandSeparator="."
                                  fixedDecimalScale={false}
                                  onValueChange={({ floatValue }) =>
                                    setFieldValue(
                                      "applicationCost",
                                      floatValue !== undefined
                                        ? floatValue
                                        : "",
                                    )
                                  }
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                        </>
                      )}
                    </CardDietItemDetails>
                  )}
                  <Row type="flex" gutter={16} style={{ marginTop: 16 }}>
                    <Col span={24}>
                      <Form.Item label={tf.notes || "Notas Complementares"}>
                        <TextArea
                          placeholder={defaultPlaceholder}
                          value={values.notes}
                          rows={2}
                          onChange={(e) =>
                            setFieldValue("notes", e.target.value)
                          }
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Form>
                {/* Footer */}
                <Row
                  type="flex"
                  justify="end"
                  align="middle"
                  gutter={16}
                  style={{ marginTop: 16 }}
                >
                  <Col>
                    <ButtonStandard
                      type="button"
                      buttonType="type7"
                      onClick={handleCancel}
                    >
                      {translation.buttons.cancel}
                    </ButtonStandard>
                  </Col>
                  <Col>
                    <ButtonStandard
                      type="button"
                      buttonType="type6"
                      onClick={handleSubmit}
                    >
                      {translation.buttons.save}
                    </ButtonStandard>
                  </Col>
                </Row>
              </StyledFormContainer>
            </Spin>
          </Modal>
        );
      }}
    </Formik>
  );
};

export default StockItemNewModal;
